/**
 * Payment Service
 * Handles subscription, checkout, and usage tracking API calls
 */

import api from './apiService';

/**
 * Get all available subscription plans
 * @returns {Promise<Array>} List of plans
 */
export const getPlans = async () => {
  const response = await api.get('/payments/plans');
  return response.data.plans;
};

/**
 * Create a Stripe checkout session
 * @param {string} planId - The plan ID to subscribe to
 * @returns {Promise<string>} Checkout URL to redirect to
 */
export const createCheckoutSession = async (planId) => {
  const response = await api.post('/payments/create-checkout-session', {
    plan_id: planId,
  });
  return response.data.checkout_url;
};

/**
 * Get current user's subscription details
 * @returns {Promise<Object|null>} Subscription details or null if no subscription
 */
export const getSubscription = async () => {
  const response = await api.get('/payments/subscription');
  // Always return the response data so we can check dev_mode
  return response.data;
};

/**
 * Get current user's usage for the billing period
 * @returns {Promise<Object|null>} Usage details or null if no subscription
 */
export const getUsage = async () => {
  const response = await api.get('/payments/usage');
  if (response.data.has_subscription) {
    return response.data;
  }
  return null;
};

/**
 * Cancel the current subscription
 * @returns {Promise<Object>} Cancellation confirmation
 */
export const cancelSubscription = async () => {
  const response = await api.post('/payments/cancel');
  return response.data;
};

/**
 * Check if user has an active subscription
 * @returns {Promise<boolean>}
 */
export const hasActiveSubscription = async () => {
  try {
    const subscription = await getSubscription();
    return subscription && ['active', 'trialing'].includes(subscription.status);
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
};

/**
 * Check if user can upload a video (has subscription and within limits)
 * @returns {Promise<{canUpload: boolean, message: string, remaining: number|null}>}
 */
export const canUploadVideo = async () => {
  try {
    const usage = await getUsage();
    if (!usage) {
      return {
        canUpload: false,
        message: 'No active subscription. Please subscribe to upload videos.',
        remaining: null,
      };
    }

    if (usage.unlimited) {
      return {
        canUpload: true,
        message: 'Unlimited uploads available',
        remaining: null,
      };
    }

    const remaining = usage.remaining || 0;
    if (remaining <= 0) {
      return {
        canUpload: false,
        message: `Upload limit reached (${usage.video_upload_limit} videos). Upgrade to Elite for unlimited uploads.`,
        remaining: 0,
      };
    }

    return {
      canUpload: true,
      message: `${remaining} uploads remaining this billing period`,
      remaining,
    };
  } catch (error) {
    console.error('Error checking upload limit:', error);
    return {
      canUpload: false,
      message: 'Error checking subscription status',
      remaining: null,
    };
  }
};

/**
 * Format price from cents to display string
 * @param {number} cents - Price in cents
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted price string
 */
export const formatPrice = (cents, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(cents / 100);
};

const paymentService = {
  getPlans,
  createCheckoutSession,
  getSubscription,
  getUsage,
  cancelSubscription,
  hasActiveSubscription,
  canUploadVideo,
  formatPrice,
};

export default paymentService;
